import moment from "moment";
import { WeeklyWorkDayDto } from "../../../../api-client";

/**
 * A change made to a work plan WITH history: it cannot be saved directly,
 * because that would rewrite the past. It waits here until the user picks an
 * effective date and a new plan version is created.
 *
 * `clientId` is the handle for a row that has no database id yet. Without it a
 * newly drawn block could not be dragged a second time — there would be
 * nothing to address it by.
 */
export interface PendingWeeklyWorkDayChange {
  type: "create" | "update";
  weeklyWorkDay: WeeklyWorkDayDto;
  clientId?: string;
}

/**
 * The colour of "not saved yet", in the calendar and in the table alike.
 *
 * Deliberately outside the palette's working vocabulary: work blocks are
 * secondary (dark red), breaks are info (blue), and warning yellow reads as
 * "something is wrong" rather than "this is a draft". A neutral slate says
 * only that the row is in a different state.
 */
export const PENDING_COLOR = "#607D8B"

const PENDING_ID_PREFIX = "pending-";

let pendingCounter = 0;

/** Handle for a not-yet-saved row, unique within the editing session. */
export const createPendingId = (): string => `${PENDING_ID_PREFIX}${++pendingCounter}`;

export const isPendingId = (id?: string | null): boolean =>
  !!id && id.startsWith(PENDING_ID_PREFIX);

/**
 * A saved work day, a pending one, or a saved one with its pending edit
 * applied — whatever is about to be SHOWN.
 */
export interface MergedWeeklyWorkDay extends WeeklyWorkDayDto {
  /** Addresses the row on screen: the database id, or the pending `clientId`. */
  displayId: string;
  /** True while the row is only a pending change, not yet applied. */
  isPending: boolean;
}

/**
 * Times reach us in three shapes: an ISO string from the API, a Date from the
 * table's time pickers, and "HH:mm" from the table's edit form. Parsing them
 * in one place keeps a pending row from silently rendering as "Invalid date".
 */
export function parseWorkDayTime(value?: string | Date | null): moment.Moment | null {
  if (!value) return null;
  if (typeof value !== "string") {
    const fromDate = moment(value);
    return fromDate.isValid() ? fromDate : null;
  }
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
    return moment(value, ["HH:mm:ss", "HH:mm"]);
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
}

export function formatWorkDayTime(value: string | Date | undefined | null, format: string): string {
  const parsed = parseWorkDayTime(value);
  return parsed ? parsed.format(format) : ""; 
}

/** Hours as German users read them: 37,00 — not 37.00. */
export function formatHours(hours: number): string {
  return (Number.isFinite(hours) ? hours : 0).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Length of one row in minutes; 0 when either end is unparseable. */
export function workDayDurationMinutes(workDay: WeeklyWorkDayDto): number {
  const start = parseWorkDayTime(workDay.startTime);
  const end = parseWorkDayTime(workDay.endTime);
  if (!start || !end) return 0;
  return Math.max(0, end.diff(start, "minutes"));
}

/**
 * The saved week with the pending changes laid over it — the single source for
 * everything the user looks at while editing a versioned plan.
 *
 * Both displays (the calendar and the table with its hour totals) read it, so
 * they cannot disagree with each other or with the pending-changes banner.
 * That disagreement was the whole defect: the banner counted four changes
 * while the calendar and the totals still showed the saved week, which reads
 * as "my edit was lost" and invites dragging it again — or discarding it.
 *
 * Not used for SAVING: `handleVersionConfirm` builds the list it posts from
 * the same two inputs, but must post plain DTOs.
 */
export function mergePendingWorkDays(
  saved: WeeklyWorkDayDto[] | undefined,
  pending: PendingWeeklyWorkDayChange[],
): MergedWeeklyWorkDay[] {
  const merged: MergedWeeklyWorkDay[] = (saved ?? []).map((workDay) => ({
    ...workDay,
    displayId: String(workDay.id),
    isPending: false,
  }));

  for (const change of pending) {
    if (change.type === "update") {
      const index = merged.findIndex((row) => row.id != null && row.id === change.weeklyWorkDay.id);
      if (index >= 0) {
        // Keep the saved row's identity — only the edited fields move.
        merged[index] = {
          ...merged[index],
          ...change.weeklyWorkDay,
          displayId: merged[index].displayId,
          isPending: true,
        };
        continue;
      }
      // The row it refers to is gone (deleted elsewhere, or a stale change).
      // Showing it is still better than dropping it silently: it is listed in
      // the banner and will be sent on apply.
    }

    merged.push({
      ...change.weeklyWorkDay,
      displayId: change.clientId ?? createPendingId(),
      isPending: true,
    });
  }

  return merged;
}
